/**
 * CraftingScreen - Minecraft-style crafting UI
 *
 * Shows a 2x2 (hand) or 3x3 (crafting table) grid, a result slot,
 * and the player inventory below. Items can be clicked to move between
 * grid and inventory. Crafting the result consumes grid ingredients.
 */

import { BlockId } from '../world/BlockType.js';
import { BLOCK_COLORS, BLOCK_TOP_COLORS, BLOCK_NAMES, renderBlockIcon } from './Hotbar.js';
import { isTool, getToolName, getToolColor } from '../items/ToolSystem.js';
import { ItemId, getItemName as registryItemName, getItemColor as registryItemColor } from '../items/ItemRegistry.js';
import { MAX_STACK_SIZE } from '../constants.js';

const SLOT_SIZE = 44;
const GAP = 2;

/**
 * Render an item icon (block, special item, or tool) as HTML
 */
function renderItemIcon(item, size) {
  if (!item) return '';

  // Tool item
  if (isTool(item)) {
    return renderToolIcon(item, size);
  }

  // Special non-block item (like sticks)
  if (item.itemId !== undefined) {
    return renderSpecialItemIcon(item, size);
  }

  // Regular block
  if (item.blockId !== undefined) {
    return renderBlockIcon(item.blockId, size);
  }

  return '';
}

/**
 * Render a tool as a simple colored icon with tool shape
 */
function renderToolIcon(item, size) {
  const color = getToolColor(item);
  const handleColor = '#8B6B3E';
  const s = size || 28;

  // Simple tool representation: colored head + brown handle
  const shapes = {
    pickaxe: `
      <div style="position:absolute;top:2px;left:2px;width:${s-6}px;height:${Math.floor(s*0.3)}px;background:${color};border:1px solid rgba(0,0,0,0.3);border-radius:1px;"></div>
      <div style="position:absolute;top:${Math.floor(s*0.3)+1}px;left:${Math.floor(s/2)-2}px;width:4px;height:${Math.floor(s*0.6)}px;background:${handleColor};border:1px solid rgba(0,0,0,0.2);"></div>
    `,
    axe: `
      <div style="position:absolute;top:2px;left:2px;width:${Math.floor(s*0.5)}px;height:${Math.floor(s*0.4)}px;background:${color};border:1px solid rgba(0,0,0,0.3);border-radius:1px 1px 0 1px;"></div>
      <div style="position:absolute;top:${Math.floor(s*0.35)}px;left:${Math.floor(s/2)-2}px;width:4px;height:${Math.floor(s*0.55)}px;background:${handleColor};border:1px solid rgba(0,0,0,0.2);"></div>
    `,
    shovel: `
      <div style="position:absolute;top:2px;left:${Math.floor(s/2)-5}px;width:10px;height:${Math.floor(s*0.3)}px;background:${color};border:1px solid rgba(0,0,0,0.3);border-radius:3px 3px 0 0;"></div>
      <div style="position:absolute;top:${Math.floor(s*0.3)}px;left:${Math.floor(s/2)-2}px;width:4px;height:${Math.floor(s*0.6)}px;background:${handleColor};border:1px solid rgba(0,0,0,0.2);"></div>
    `,
    sword: `
      <div style="position:absolute;top:2px;left:${Math.floor(s/2)-3}px;width:6px;height:${Math.floor(s*0.55)}px;background:${color};border:1px solid rgba(0,0,0,0.3);"></div>
      <div style="position:absolute;top:${Math.floor(s*0.5)}px;left:${Math.floor(s/2)-6}px;width:12px;height:3px;background:#555;border:1px solid rgba(0,0,0,0.3);"></div>
      <div style="position:absolute;top:${Math.floor(s*0.55)+2}px;left:${Math.floor(s/2)-2}px;width:4px;height:${Math.floor(s*0.35)}px;background:${handleColor};border:1px solid rgba(0,0,0,0.2);"></div>
    `,
    hoe: `
      <div style="position:absolute;top:2px;left:2px;width:${Math.floor(s*0.45)}px;height:${Math.floor(s*0.25)}px;background:${color};border:1px solid rgba(0,0,0,0.3);"></div>
      <div style="position:absolute;top:${Math.floor(s*0.25)}px;left:${Math.floor(s/2)-2}px;width:4px;height:${Math.floor(s*0.65)}px;background:${handleColor};border:1px solid rgba(0,0,0,0.2);"></div>
    `,
  };

  return `
    <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
      ${shapes[item.toolType] || ''}
    </div>
  `;
}

/**
 * Render a special non-block item (stick, crafting table item, furnace item)
 */
function renderSpecialItemIcon(item, size) {
  const s = size || 28;
  const color = registryItemColor(item.itemId) || '#888';

  if (item.itemId === ItemId.STICK) {
    // Brown stick diagonal
    return `
      <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
        <div style="position:absolute;top:3px;left:${Math.floor(s/2)-2}px;width:4px;height:${s-6}px;background:${color};
          border:1px solid rgba(0,0,0,0.25);transform:rotate(-10deg);border-radius:1px;"></div>
      </div>
    `;
  }

  if (item.itemId === ItemId.CRAFTING_TABLE) {
    // Mini crafting table: wood block with grid lines on top
    return `
      <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
        <div style="position:absolute;bottom:0;left:1px;width:${s-6}px;height:${s-6}px;background:#8B6B3E;
          border:2px solid rgba(0,0,0,0.25);
          box-shadow:inset ${Math.floor(s/8)}px ${Math.floor(s/8)}px 0 rgba(255,255,255,0.2);"></div>
        <div style="position:absolute;top:0;left:1px;width:${s-6}px;height:${Math.floor(s*0.35)}px;background:#B89050;
          border:2px solid rgba(0,0,0,0.15);"></div>
        <div style="position:absolute;top:${Math.floor(s*0.15)}px;left:${Math.floor(s*0.35)}px;width:1px;height:${Math.floor(s*0.2)}px;background:rgba(0,0,0,0.3);"></div>
        <div style="position:absolute;top:${Math.floor(s*0.25)}px;left:3px;width:${s-10}px;height:1px;background:rgba(0,0,0,0.3);"></div>
      </div>
    `;
  }

  if (item.itemId === ItemId.FURNACE) {
    // Mini furnace: stone block with dark opening
    return `
      <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
        <div style="position:absolute;bottom:0;left:1px;width:${s-6}px;height:${s-6}px;background:#7A7A7A;
          border:2px solid rgba(0,0,0,0.25);
          box-shadow:inset ${Math.floor(s/8)}px ${Math.floor(s/8)}px 0 rgba(255,255,255,0.15);"></div>
        <div style="position:absolute;top:${Math.floor(s*0.35)}px;left:${Math.floor(s*0.3)}px;
          width:${Math.floor(s*0.35)}px;height:${Math.floor(s*0.35)}px;background:#2A2A2A;border:1px solid #1A1A1A;"></div>
      </div>
    `;
  }

  // Fallback: colored square
  return `
    <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
      <div style="position:absolute;bottom:0;left:1px;width:${s-6}px;height:${s-6}px;background:${color};
        border:2px solid rgba(0,0,0,0.25);"></div>
    </div>
  `;
}

/**
 * Get display name for any item (block, special item, or tool)
 */
function getItemName(item) {
  if (!item) return '';
  if (isTool(item)) return getToolName(item);
  if (item.itemId !== undefined) return registryItemName(item.itemId) || 'Item';
  if (item.blockId !== undefined) return BLOCK_NAMES[item.blockId] || 'Bloque';
  return '';
}


export class CraftingScreen {
  constructor(inventory, craftingSystem) {
    this.inventory = inventory;
    this.craftingSystem = craftingSystem;
    this._isOpen = false;
    this._is3x3 = false;
    this.heldItem = null;
    this.heldFrom = null; // { area, index } or { area: 'grid', row, col } or { area: 'result' }
    this._soundManager = null;

    // Crafting grid contents (3x3 max, use subset for 2x2)
    this.grid = [
      [null, null, null],
      [null, null, null],
      [null, null, null],
    ];
    this.resultItem = null;

    this._buildDOM();
  }

  get isOpen() {
    return this._isOpen;
  }

  _buildDOM() {
    // --- Overlay ---
    this.overlay = document.createElement('div');
    this.overlay.id = 'crafting-overlay';
    this.overlay.style.cssText = `
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.55);
      z-index: 500;
      display: none;
      justify-content: center;
      align-items: center;
      cursor: default;
    `;

    // --- Main panel ---
    this.panel = document.createElement('div');
    this.panel.style.cssText = `
      background: #C6C6C6;
      border: 4px solid #2A2A2A;
      border-top-color: #FFFFFF;
      border-left-color: #FFFFFF;
      padding: 8px;
      font-family: 'Courier New', monospace;
      color: #404040;
      user-select: none;
      image-rendering: pixelated;
      box-shadow: 4px 4px 0 rgba(0,0,0,0.3), inset 0 0 0 2px #8B8B8B;
      max-height: 90vh;
      overflow-y: auto;
    `;

    // Title
    this.titleEl = document.createElement('div');
    this.titleEl.style.cssText = `
      font-size: 14px;
      margin-bottom: 8px;
      color: #404040;
      text-shadow: 1px 1px 0 rgba(255,255,255,0.4);
      font-weight: bold;
      text-align: center;
    `;
    this.panel.appendChild(this.titleEl);

    // === CRAFTING AREA (grid + arrow + result) ===
    this.craftingArea = document.createElement('div');
    this.craftingArea.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      margin-bottom: 12px;
      padding: 8px;
      background: #B0B0B0;
      border: 2px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
    `;

    // Crafting grid container
    this.gridContainer = document.createElement('div');
    this.gridSlots = [];
    this.craftingArea.appendChild(this.gridContainer);

    // Arrow
    const arrow = document.createElement('div');
    arrow.style.cssText = `
      font-size: 28px;
      color: #555;
      padding: 0 4px;
      line-height: 1;
    `;
    arrow.textContent = '\u2192'; // right arrow
    this.craftingArea.appendChild(arrow);

    // Result slot
    this.resultSlot = document.createElement('div');
    this.resultSlot.style.cssText = `
      width: ${SLOT_SIZE + 8}px;
      height: ${SLOT_SIZE + 8}px;
      background: #8B8B8B;
      border: 3px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
    `;
    this.resultSlot.addEventListener('mouseenter', () => {
      this.resultSlot.style.background = '#A0A0A0';
      if (this.resultItem) {
        this.tooltip.textContent = getItemName(this.resultItem);
        this.tooltip.style.display = 'block';
      }
    });
    this.resultSlot.addEventListener('mouseleave', () => {
      this.resultSlot.style.background = '#8B8B8B';
      this.tooltip.style.display = 'none';
    });
    this.resultSlot.addEventListener('click', () => {
      this._onResultClick();
    });
    this.craftingArea.appendChild(this.resultSlot);

    this.panel.appendChild(this.craftingArea);

    // === Separator ===
    const sep = document.createElement('div');
    sep.style.cssText = `
      height: 2px;
      background: linear-gradient(to right, transparent, #5A5A5A 10%, #5A5A5A 90%, transparent);
      margin: 6px 0;
    `;
    this.panel.appendChild(sep);

    // === INVENTORY SECTION ===
    const invTitle = document.createElement('div');
    invTitle.textContent = 'Inventario';
    invTitle.style.cssText = `
      font-size: 14px;
      margin-bottom: 6px;
      color: #404040;
      text-shadow: 1px 1px 0 rgba(255,255,255,0.4);
      font-weight: bold;
    `;
    this.panel.appendChild(invTitle);

    // Storage grid (3 rows x 9 cols)
    this.storageGrid = document.createElement('div');
    this.storageGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE}px);
      gap: ${GAP}px;
      margin-bottom: 8px;
    `;
    this.storageSlots = [];
    for (let i = 0; i < 27; i++) {
      const slot = this._createInventorySlot('storage', i);
      this.storageSlots.push(slot);
      this.storageGrid.appendChild(slot);
    }
    this.panel.appendChild(this.storageGrid);

    // Separator
    const sep2 = document.createElement('div');
    sep2.style.cssText = `
      height: 2px;
      background: linear-gradient(to right, transparent, #8B8B8B 10%, #8B8B8B 90%, transparent);
      margin: 6px 0;
    `;
    this.panel.appendChild(sep2);

    // Hotbar row
    this.hotbarGrid = document.createElement('div');
    this.hotbarGrid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(9, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;
    this.hotbarSlotEls = [];
    for (let i = 0; i < 9; i++) {
      const slot = this._createInventorySlot('hotbar', i);
      this.hotbarSlotEls.push(slot);
      this.hotbarGrid.appendChild(slot);
    }
    this.panel.appendChild(this.hotbarGrid);

    this.overlay.appendChild(this.panel);

    // --- Held item cursor ---
    this.cursorItem = document.createElement('div');
    this.cursorItem.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 600;
      display: none;
    `;
    document.body.appendChild(this.cursorItem);

    // --- Tooltip ---
    this.tooltip = document.createElement('div');
    this.tooltip.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 600;
      background: #100010;
      color: white;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      padding: 4px 8px;
      border: 2px solid #5000A0;
      display: none;
      text-shadow: 1px 1px 0 #3F3F3F;
      white-space: nowrap;
    `;
    document.body.appendChild(this.tooltip);

    document.body.appendChild(this.overlay);

    // Mouse tracking
    this._onMouseMove = (e) => {
      if (!this._isOpen) return;
      this.cursorItem.style.left = (e.clientX + 10) + 'px';
      this.cursorItem.style.top = (e.clientY + 10) + 'px';
      this.tooltip.style.left = (e.clientX + 15) + 'px';
      this.tooltip.style.top = (e.clientY - 30) + 'px';
    };
    // mousemove listener added in open(), removed in close()
  }

  /**
   * Build the crafting grid slots (called on open to set correct size)
   */
  _buildGrid() {
    this.gridContainer.innerHTML = '';
    this.gridSlots = [];
    const size = this._is3x3 ? 3 : 2;

    this.gridContainer.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${size}, ${SLOT_SIZE}px);
      gap: ${GAP}px;
    `;

    for (let r = 0; r < size; r++) {
      this.gridSlots[r] = [];
      for (let c = 0; c < size; c++) {
        const slot = document.createElement('div');
        slot.style.cssText = `
          width: ${SLOT_SIZE}px;
          height: ${SLOT_SIZE}px;
          background: #8B8B8B;
          border: 2px solid;
          border-color: #373737 #FFFFFF #FFFFFF #373737;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          cursor: pointer;
        `;

        const row = r, col = c;
        slot.addEventListener('mouseenter', () => {
          slot.style.background = '#A0A0A0';
          const item = this.grid[row][col];
          if (item) {
            this.tooltip.textContent = getItemName(item);
            this.tooltip.style.display = 'block';
          }
        });
        slot.addEventListener('mouseleave', () => {
          slot.style.background = '#8B8B8B';
          this.tooltip.style.display = 'none';
        });
        slot.addEventListener('click', () => {
          this._onGridSlotClick(row, col);
        });
        slot.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          this._onGridSlotRightClick(row, col);
        });

        this.gridSlots[r][c] = slot;
        this.gridContainer.appendChild(slot);
      }
    }
  }

  /**
   * Create an inventory slot element (for storage or hotbar)
   */
  _createInventorySlot(area, index) {
    const slot = document.createElement('div');
    slot.style.cssText = `
      width: ${SLOT_SIZE}px;
      height: ${SLOT_SIZE}px;
      background: #8B8B8B;
      border: 2px solid;
      border-color: #373737 #FFFFFF #FFFFFF #373737;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      cursor: pointer;
    `;

    slot.addEventListener('mouseenter', () => {
      slot.style.background = '#A0A0A0';
      const item = this.inventory.getItem(area, index);
      if (item) {
        this.tooltip.textContent = getItemName(item);
        this.tooltip.style.display = 'block';
      }
    });
    slot.addEventListener('mouseleave', () => {
      slot.style.background = '#8B8B8B';
      this.tooltip.style.display = 'none';
    });
    slot.addEventListener('click', () => {
      this._onInventorySlotClick(area, index);
    });
    slot.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this._onInventorySlotRightClick(area, index);
    });

    return slot;
  }

  // ============================
  //  CLICK HANDLERS
  // ============================

  _onGridSlotClick(row, col) {
    const gridCell = this.grid[row][col];

    if (this.heldItem) {
      if (!gridCell) {
        // Place held item (1 unit) into grid
        this.grid[row][col] = this._takeOne(this.heldItem);
        if (this.heldItem.count !== undefined && this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      } else if (this._canStack(gridCell, this.heldItem)) {
        // Stack if same type (grid slots are single items for crafting, but allow stacking)
        const space = MAX_STACK_SIZE - (gridCell.count || 1);
        if (space > 0) {
          gridCell.count = (gridCell.count || 1) + 1;
          this._removeOne(this.heldItem);
          if (this.heldItem.count !== undefined && this.heldItem.count <= 0) {
            this.heldItem = null;
            this.heldFrom = null;
          }
        }
      } else {
        // Swap
        const temp = { ...gridCell };
        this.grid[row][col] = { ...this.heldItem };
        this.heldItem = temp;
      }
    } else {
      if (gridCell) {
        this.heldItem = { ...gridCell };
        this.heldFrom = { area: 'grid', row, col };
        this.grid[row][col] = null;
      }
    }

    this._updateRecipe();
    this._render();
  }

  _onGridSlotRightClick(row, col) {
    const gridCell = this.grid[row][col];

    if (this.heldItem) {
      if (!gridCell) {
        // Place one item from held into grid
        this.grid[row][col] = this._takeOne(this.heldItem);
        if (this.heldItem.count !== undefined && this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      }
    } else {
      if (gridCell && gridCell.count !== undefined && gridCell.count > 1) {
        const half = Math.ceil(gridCell.count / 2);
        this.heldItem = { ...gridCell, count: half };
        gridCell.count -= half;
      } else if (gridCell) {
        this.heldItem = { ...gridCell };
        this.grid[row][col] = null;
      }
    }

    this._updateRecipe();
    this._render();
  }

  /** Connect sound manager for craft sounds */
  setSoundManager(sm) {
    this._soundManager = sm;
  }

  _onResultClick() {
    if (!this.resultItem) return;

    if (this.heldItem) {
      // Can only pick up result if hands are empty or stacking same type
      if (this._canStack(this.heldItem, this.resultItem)) {
        const toAdd = this.resultItem.count || 1;
        this.heldItem.count = (this.heldItem.count || 1) + toAdd;
        this._consumeGrid();
        this._updateRecipe();
        this._render();
        if (this._soundManager) this._soundManager.playCraftSuccess();
      }
      return;
    }

    // Pick up crafted result
    this.heldItem = { ...this.resultItem };
    this.heldFrom = { area: 'result' };
    this._consumeGrid();
    this._updateRecipe();
    this._render();
    if (this._soundManager) this._soundManager.playCraftSuccess();
  }

  _onInventorySlotClick(area, index) {
    const clickedItem = this.inventory.getItem(area, index);

    if (this.heldItem) {
      if (!clickedItem) {
        this.inventory.setItem(area, index, { ...this.heldItem });
        this.heldItem = null;
        this.heldFrom = null;
      } else if (this._canStack(clickedItem, this.heldItem)) {
        const space = MAX_STACK_SIZE - (clickedItem.count || 1);
        const toMove = Math.min(space, this.heldItem.count || 1);
        clickedItem.count = (clickedItem.count || 1) + toMove;
        if (this.heldItem.count !== undefined) {
          this.heldItem.count -= toMove;
          if (this.heldItem.count <= 0) {
            this.heldItem = null;
            this.heldFrom = null;
          }
        } else {
          this.heldItem = null;
          this.heldFrom = null;
        }
      } else {
        // Swap
        this.inventory.setItem(area, index, { ...this.heldItem });
        this.heldItem = { ...clickedItem };
        this.heldFrom = { area, index };
      }
    } else {
      if (clickedItem) {
        this.heldItem = { ...clickedItem };
        this.heldFrom = { area, index };
        this.inventory.setItem(area, index, null);
      }
    }

    this._render();
  }

  _onInventorySlotRightClick(area, index) {
    const clickedItem = this.inventory.getItem(area, index);

    if (this.heldItem) {
      if (!clickedItem) {
        // Place one
        const one = this._takeOne(this.heldItem);
        this.inventory.setItem(area, index, one);
        if (this.heldItem.count !== undefined && this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      } else if (this._canStack(clickedItem, this.heldItem) && (clickedItem.count || 1) < MAX_STACK_SIZE) {
        clickedItem.count = (clickedItem.count || 1) + 1;
        this._removeOne(this.heldItem);
        if (this.heldItem.count !== undefined && this.heldItem.count <= 0) {
          this.heldItem = null;
          this.heldFrom = null;
        }
      }
    } else {
      if (clickedItem && clickedItem.count !== undefined && clickedItem.count > 1) {
        const half = Math.ceil(clickedItem.count / 2);
        this.heldItem = { ...clickedItem, count: half };
        clickedItem.count -= half;
        this.heldFrom = { area, index };
      } else if (clickedItem) {
        this.heldItem = { ...clickedItem };
        this.heldFrom = { area, index };
        this.inventory.setItem(area, index, null);
      }
    }

    this._render();
  }

  // ============================
  //  ITEM HELPERS
  // ============================

  /**
   * Check if two items can be stacked (same blockId/itemId, both not tools)
   */
  _canStack(a, b) {
    if (!a || !b) return false;
    if (isTool(a) || isTool(b)) return false;
    if (a.blockId !== undefined && b.blockId !== undefined) return a.blockId === b.blockId;
    if (a.itemId !== undefined && b.itemId !== undefined) return a.itemId === b.itemId;
    return false;
  }

  /**
   * Take one unit from an item, returning a new single-unit item.
   * Decrements source count.
   */
  _takeOne(item) {
    if (isTool(item)) {
      // Tools don't stack; return the whole tool, source becomes null
      return { ...item };
    }
    const one = { ...item, count: 1 };
    if (item.count !== undefined) {
      item.count--;
    }
    return one;
  }

  /**
   * Remove one unit from an item stack
   */
  _removeOne(item) {
    if (item.count !== undefined) {
      item.count--;
    }
  }

  // ============================
  //  CRAFTING LOGIC
  // ============================

  /**
   * Update the result slot based on current grid contents
   */
  _updateRecipe() {
    const size = this._is3x3 ? 3 : 2;
    // Build the grid array for the crafting system
    const gridArray = [];
    for (let r = 0; r < size; r++) {
      gridArray[r] = [];
      for (let c = 0; c < size; c++) {
        const cell = this.grid[r][c];
        if (cell) {
          // Pass the identifying properties to the crafting system
          if (cell.blockId !== undefined) {
            gridArray[r][c] = { blockId: cell.blockId };
          } else if (cell.itemId !== undefined) {
            gridArray[r][c] = { itemId: cell.itemId };
          } else {
            gridArray[r][c] = null;
          }
        } else {
          gridArray[r][c] = null;
        }
      }
    }

    this.resultItem = this.craftingSystem.checkRecipe(gridArray);
  }

  /**
   * Consume one set of ingredients from the grid after crafting
   */
  _consumeGrid() {
    const size = this._is3x3 ? 3 : 2;
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const cell = this.grid[r][c];
        if (cell) {
          if (cell.count !== undefined && cell.count > 1) {
            cell.count--;
          } else {
            this.grid[r][c] = null;
          }
        }
      }
    }
  }

  /**
   * Return all grid items back to the inventory (on close)
   */
  _returnGridToInventory() {
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        const cell = this.grid[r][c];
        if (cell) {
          let added = false;
          if (cell.blockId !== undefined) {
            added = true;
            for (let i = 0; i < (cell.count || 1); i++) {
              if (!this.inventory.addBlock(cell.blockId)) {
                console.warn('[Crafting] Inventario lleno — bloque perdido:', cell.blockId);
                added = false;
              }
            }
          }
          // For special items and tools, try to add to an empty slot
          else if (cell.itemId !== undefined || isTool(cell)) {
            added = this._addItemToInventory(cell);
            if (!added) {
              console.warn('[Crafting] Inventario lleno — item perdido:', cell);
            }
          }
          this.grid[r][c] = null;
        }
      }
    }
  }

  /**
   * Try to add a non-block item to the inventory
   */
  _addItemToInventory(item) {
    if (!item) return false;

    // If it's a regular block, use inventory.addBlock
    if (item.blockId !== undefined) {
      for (let i = 0; i < (item.count || 1); i++) {
        this.inventory.addBlock(item.blockId);
      }
      return true;
    }

    // For special items and tools, find an empty slot
    // Try stacking for non-tool items
    if (!isTool(item) && item.itemId !== undefined) {
      const count = item.count || 1;
      // Try stacking in hotbar
      for (let i = 0; i < this.inventory.slots.length; i++) {
        const s = this.inventory.slots[i];
        if (s && s.itemId === item.itemId && (s.count || 0) < MAX_STACK_SIZE) {
          const space = MAX_STACK_SIZE - (s.count || 0);
          const toAdd = Math.min(space, count);
          s.count = (s.count || 0) + toAdd;
          return true;
        }
      }
      // Try stacking in storage
      for (let i = 0; i < this.inventory.storage.length; i++) {
        const s = this.inventory.storage[i];
        if (s && s.itemId === item.itemId && (s.count || 0) < MAX_STACK_SIZE) {
          const space = MAX_STACK_SIZE - (s.count || 0);
          const toAdd = Math.min(space, count);
          s.count = (s.count || 0) + toAdd;
          return true;
        }
      }
    }

    // Find empty hotbar slot
    for (let i = 0; i < this.inventory.slots.length; i++) {
      if (!this.inventory.slots[i]) {
        this.inventory.slots[i] = { ...item };
        return true;
      }
    }
    // Find empty storage slot
    for (let i = 0; i < this.inventory.storage.length; i++) {
      if (!this.inventory.storage[i]) {
        this.inventory.storage[i] = { ...item };
        return true;
      }
    }
    return false;
  }

  // ============================
  //  RENDERING
  // ============================

  _render() {
    const size = this._is3x3 ? 3 : 2;

    // Render grid slots
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        this._renderSlotEl(this.gridSlots[r][c], this.grid[r][c]);
      }
    }

    // Render result slot
    if (this.resultItem) {
      const iconSize = 32;
      this.resultSlot.innerHTML = `
        ${renderItemIcon(this.resultItem, iconSize)}
        <span style="font-size:11px;font-weight:bold;position:absolute;bottom:2px;right:4px;color:white;
          text-shadow:2px 2px 0 #3F3F3F, -1px 0 0 #3F3F3F;
          font-family:'Courier New',monospace;">
          ${(this.resultItem.count || 1) > 1 ? (this.resultItem.count || 1) : ''}
        </span>
      `;
    } else {
      this.resultSlot.innerHTML = '';
    }

    // Render inventory slots
    for (let i = 0; i < 27; i++) {
      this._renderSlotEl(this.storageSlots[i], this.inventory.storage[i]);
    }
    for (let i = 0; i < 9; i++) {
      const el = this.hotbarSlotEls[i];
      this._renderSlotEl(el, this.inventory.slots[i]);
      if (i === this.inventory.selectedIndex) {
        el.style.borderColor = '#FFFFFF #373737 #373737 #FFFFFF';
        el.style.background = '#A8A8A8';
      }
    }

    // Render held item on cursor
    if (this.heldItem && ((this.heldItem.toolId !== undefined) || (this.heldItem.count !== undefined && this.heldItem.count > 0))) {
      this.cursorItem.style.display = 'block';
      const count = this.heldItem.count || 1;
      this.cursorItem.innerHTML = `
        <div style="position:relative;">
          ${renderItemIcon(this.heldItem, 32)}
          <span style="
            position:absolute;bottom:-2px;right:-2px;
            font-family:'Courier New',monospace;font-size:12px;font-weight:bold;
            color:white;text-shadow:2px 2px 0 #3F3F3F;
          ">${count > 1 ? count : ''}</span>
        </div>
      `;
    } else {
      this.cursorItem.style.display = 'none';
    }
  }

  _renderSlotEl(el, item) {
    if (item && ((item.count !== undefined && item.count > 0) || isTool(item) || item.toolType)) {
      const count = item.count || 1;
      el.innerHTML = `
        ${renderItemIcon(item, 28)}
        <span style="font-size:11px;font-weight:bold;position:absolute;bottom:1px;right:3px;color:white;
          text-shadow:2px 2px 0 #3F3F3F, -1px 0 0 #3F3F3F;
          font-family:'Courier New',monospace;">${count > 1 ? count : ''}</span>
      `;
    } else {
      el.innerHTML = '';
    }
  }

  // ============================
  //  PUBLIC API
  // ============================

  open(is3x3 = false) {
    if (this._isOpen) return;
    this._isOpen = true;
    this._is3x3 = is3x3;
    this.heldItem = null;
    this.heldFrom = null;

    // Clear grid
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        this.grid[r][c] = null;
      }
    }
    this.resultItem = null;

    // Update title
    this.titleEl.textContent = is3x3 ? 'Mesa de Crafteo' : 'Crafteo';

    // Build grid with correct size
    this._buildGrid();

    document.exitPointerLock();
    this.overlay.style.display = 'flex';
    document.addEventListener('mousemove', this._onMouseMove);
    this._render();
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.overlay.style.display = 'none';
    this.cursorItem.style.display = 'none';
    this.tooltip.style.display = 'none';

    document.removeEventListener('mousemove', this._onMouseMove);

    // Return held item to inventory (drop if full)
    if (this.heldItem) {
      if (!this._addItemToInventory(this.heldItem)) {
        console.warn('[Crafting] Inventario lleno — item perdido al cerrar:', this.heldItem);
      }
      this.heldItem = null;
      this.heldFrom = null;
    }

    // Return grid items to inventory (warn if any lost)
    this._returnGridToInventory();
    this.resultItem = null;
  }

  toggle(is3x3 = false) {
    if (this._isOpen) {
      this.close();
    } else {
      this.open(is3x3);
    }
  }

  update() {
    if (!this._isOpen) return;
    // Re-render in case inventory changed externally
    this._render();
  }
}
